// src/features/auth/authAPI.js
import api from '../../services/api';

// "Trust this device" token. Deliberately NOT cleared on logout — the whole
// point is that this browser stays trusted across sessions until it expires
// server-side (or the user resets their password, which revokes it).
export const MFA_DEVICE_TOKEN_KEY = 'mfa_device_token';

export const getMfaDeviceToken = () => {
  try {
    return localStorage.getItem(MFA_DEVICE_TOKEN_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export const setMfaDeviceToken = (token) => {
  try {
    if (token) localStorage.setItem(MFA_DEVICE_TOKEN_KEY, token);
  } catch {
    /* private mode / storage disabled — the user just gets asked for a code */
  }
};

export const login = async ({ employee_code, password, org_code, tenant_key }) => {
  // Adjust the payload to match your back-end's requirement
  const payload = { employee_code, password };
  if (org_code) payload.org_code = org_code;
  if (tenant_key) payload.tenant_key = tenant_key;

  // Lets the server skip the emailed code on a browser the user already
  // trusted. An unknown or expired token is ignored, never an error.
  const deviceToken = getMfaDeviceToken();
  if (deviceToken) payload.device_token = deviceToken;

  const response = await api.post('/login', payload, { skipAuthRefresh: true });
  return response.data; // { user, token } or { mfa_required, challenge_id, ... }
};

// skipAuthRefresh is mandatory on both calls below: api.js only treats
// /login and /refresh as auth requests, so a 400/401 here would otherwise fall
// into the token-refresh branch and hard-redirect to /login, wiping the
// pending challenge mid-flow.
export const verifyMfaCode = async ({ challenge_id, code, remember_device, org_code, tenant_key }) => {
  const payload = { challenge_id, code };
  if (remember_device) payload.remember_device = true;
  if (org_code) payload.org_code = org_code;
  if (tenant_key) payload.tenant_key = tenant_key;

  const response = await api.post('/login/mfa/verify', payload, { skipAuthRefresh: true });
  return response.data;
};

export const resendMfaCode = async ({ challenge_id, org_code, tenant_key }) => {
  const payload = { challenge_id };
  if (org_code) payload.org_code = org_code;
  if (tenant_key) payload.tenant_key = tenant_key;

  // allowDuplicate: the mutation dedupe guard in api.js would silently no-op a
  // second Resend with the same body within 600ms of the first succeeding.
  const response = await api.post('/login/mfa/resend', payload, {
    skipAuthRefresh: true,
    allowDuplicate: true,
  });
  return response.data;
};
